import { useId, useRef } from 'react';
import { Button, Spinner } from 'react-bootstrap';
import { isTppProofFileAllowed, TPP_PROOF_ACCEPT } from '../../../services/systems/transit-pass-pool/tppProofFile';

type TppProofUploadButtonProps = {
  hasProof: boolean;
  pending: boolean;
  label: string;
  replaceLabel: string;
  onFile: (file: File) => void;
  /** 許可外タイプ選択時（任意） */
  onReject?: () => void;
  /** link 見た目（差し替え）か outline ボタン（新規添付） */
  linkStyle?: boolean;
  /** モバイル履歴など密度優先（文言は aria-label / title のみ） */
  iconOnly?: boolean;
};

/** 隠し file input + Button。a11y 上は button 1 つだけにする */
export default function TppProofUploadButton({
  hasProof,
  pending,
  label,
  replaceLabel,
  onFile,
  onReject,
  linkStyle = false,
  iconOnly = false,
}: TppProofUploadButtonProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const inputId = useId();
  const name = hasProof ? replaceLabel : label;

  return (
    <>
      <input
        id={inputId}
        ref={inputRef}
        type="file"
        accept={TPP_PROOF_ACCEPT}
        disabled={pending}
        className="visually-hidden"
        tabIndex={-1}
        aria-hidden
        onChange={(e) => {
          const f = e.target.files?.[0];
          e.target.value = '';
          if (!f) return;
          if (!isTppProofFileAllowed(f)) {
            onReject?.();
            return;
          }
          onFile(f);
        }}
      />
      <Button
        type="button"
        size="sm"
        variant={linkStyle || hasProof ? 'link' : 'outline-primary'}
        className={linkStyle || hasProof ? 'p-0 text-decoration-none' : undefined}
        disabled={pending}
        aria-label={name}
        title={name}
        onClick={() => inputRef.current?.click()}
      >
        {pending ? (
          <Spinner animation="border" size="sm" role="status" />
        ) : (
          <>
            {!hasProof ? <i className={`fas fa-upload${iconOnly ? '' : ' me-1'}`} aria-hidden /> : null}
            {!iconOnly ? <span aria-hidden="true">{name}</span> : null}
            {iconOnly && hasProof ? <i className="fas fa-sync-alt" aria-hidden /> : null}
          </>
        )}
      </Button>
    </>
  );
}
